import React, { useCallback, useEffect, useRef, useState } from 'react'
import {
  Button,
  Card,
  Empty,
  Grid,
  Image,
  Input,
  Pagination,
  Result,
  Segmented,
  Select,
  Space,
  Spin,
  Switch,
  Table,
  Tag,
  Tooltip,
  Typography,
  message
} from 'antd'
import {
  AppstoreOutlined,
  ReloadOutlined,
  SearchOutlined,
  SettingOutlined
} from '@ant-design/icons'
import styled from 'styled-components'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { useMembership } from '../../hooks/useMembership'
import { usePermission } from '../../hooks/usePermission'
import { getApiErrorMessage } from '../../utils/api'
import { getProductImageUrl } from '../../utils/productImage'
import { getProducts, getCategories } from '../../api/store'
import { toggleProductOnline } from '../../api/shop'
import ProductVariantModal from '../../components/shop/ProductVariantModal'
import ShopHint from '../../components/shop/ShopHint'
import ResponsiveSelect from '../../components/common/ResponsiveSelect'

const { Text } = Typography

const PageWrapper = styled.div`
  min-height: calc(100vh - 120px);
  padding: 24px;
  background: #f5f5f5;

  @media (max-width: 768px) {
    padding: 12px;
  }
`

const ContentWrapper = styled.div`
  max-width: 1400px;
  margin: 0 auto;
`

const FilterBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 16px;
`

/* ── 手機版商品卡片：一筆商品一張卡，取代橫向捲動的表格 ── */
const MobileCard = styled.div`
  border: 1px solid #eef0f5;
  border-radius: 14px;
  background: #fff;
  padding: 14px;
  margin-bottom: 10px;

  .top {
    display: flex;
    gap: 12px;
    align-items: flex-start;
  }

  .thumb {
    width: 56px;
    height: 56px;
    border-radius: 10px;
    object-fit: cover;
    background: #f0f0f0;
    flex-shrink: 0;
  }

  .thumb-empty {
    width: 56px;
    height: 56px;
    border-radius: 10px;
    background: #f0f0f0;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #bbb;
    flex-shrink: 0;
  }

  .info {
    flex: 1;
    min-width: 0;
  }

  .name {
    font-weight: 600;
    color: #3b3540;
    line-height: 1.5;
    overflow-wrap: anywhere;
  }

  .meta {
    font-size: 12px;
    color: #999;
    margin-top: 2px;
  }

  .price {
    font-weight: 600;
    color: #d5728a;
    margin-top: 4px;
  }

  .bottom {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    margin-top: 12px;
    padding-top: 12px;
    border-top: 1px dashed #f0ecf2;
  }

  .blocked-reason {
    font-size: 11px;
    color: #c86e6e;
    margin-top: 4px;
  }
`

const money = (n) => `NT$ ${Number(n || 0).toLocaleString('en-US')}`

export default function ShopProductsPage () {
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams, setSearchParams] = useSearchParams()
  const { hasFeature } = useMembership()
  const { can } = usePermission()
  const canManage = can('shopSetting:manage')

  // 手機（< md 768px）改用卡片列表，桌機維持表格
  const screens = Grid.useBreakpoint()
  const isMobile = !screens.md

  const [loading, setLoading] = useState(false)
  const [products, setProducts] = useState([])
  const [categories, setCategories] = useState([])
  // 分頁與篩選一律以網址為準：從編輯商品頁返回時才回得到原本那一頁、
  // 原本的搜尋與分類（元件會重新掛載，state 留不住）。
  const [pagination, setPagination] = useState(() => ({
    page: Math.max(1, parseInt(searchParams.get('page'), 10) || 1),
    pageSize: 20,
    total: 0
  }))
  // 全部/販售中/未上架的「全頁面總計」（來自後端，反映目前分類/搜尋篩選，不隨換頁改變）
  const [statusCounts, setStatusCounts] = useState({
    total: 0,
    online: 0,
    offline: 0
  })
  const [onlineFilter, setOnlineFilter] = useState(
    () => searchParams.get('status') || 'all'
  )
  const [filters, setFilters] = useState(() => ({
    keyword: searchParams.get('keyword') || '',
    categoryId: searchParams.get('categoryId') || undefined
  }))
  const [togglingId, setTogglingId] = useState(null)

  const [variantProduct, setVariantProduct] = useState(null)

  const fetchCategories = useCallback(async () => {
    try {
      const res = await getCategories()
      setCategories(res.data?.data || [])
    } catch (e) {
      /* 分類載入失敗不影響主功能 */
    }
  }, [])

  const fetchProducts = useCallback(
    async (page = 1) => {
      setLoading(true)
      try {
        const res = await getProducts({
          page,
          pageSize: pagination.pageSize,
          keyword: filters.keyword || undefined,
          categoryId: filters.categoryId
        })
        const data = res.data?.data || {}
        setProducts(data.items || [])
        setPagination((p) => ({ ...p, page, total: data.total || 0 }))
        // tab 統計改用後端「篩選後總計」，不再用當頁陣列計算
        const summary = data.summary || {}
        const totalCount = data.total || 0
        const onlineTotal = summary.onlineCount || 0
        setStatusCounts({
          total: totalCount,
          online: onlineTotal,
          offline:
            summary.offlineCount != null
              ? summary.offlineCount
              : Math.max(0, totalCount - onlineTotal)
        })
      } catch (e) {
        message.error(getApiErrorMessage(e, '載入商品失敗'))
      } finally {
        setLoading(false)
      }
    },
    [filters, pagination.pageSize]
  )

  useEffect(() => {
    fetchCategories()
  }, [fetchCategories])

  /**
   * 決定要載入哪一頁。
   *
   * ⚠️ 不能用「是不是第一次執行」來判斷 —— React.StrictMode 在開發模式
   * 會把 effect 執行、清理、再執行一次，第二次就會被誤判成「篩選變了」
   * 而重置到第 1 頁；使用者按瀏覽器上一頁回到 ?page=4 時，
   * 畫面會閃一下正確頁數再跳回第一頁，網址的 page 參數也被一併洗掉。
   *
   * 改成比對篩選條件的簽章：簽章沒變就沿用目前頁數，變了才回第 1 頁。
   * 這樣 effect 跑幾次結果都一樣（冪等），不依賴執行次數。
   */
  const lastFilterSigRef = useRef(null)
  useEffect(() => {
    const sig = JSON.stringify({
      keyword: filters.keyword || '',
      categoryId: filters.categoryId || '',
      pageSize: pagination.pageSize
    })
    const isFirstRun = lastFilterSigRef.current === null
    const filtersChanged = !isFirstRun && lastFilterSigRef.current !== sig
    lastFilterSigRef.current = sig

    fetchProducts(filtersChanged ? 1 : pagination.page)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchProducts])

  // 把目前的分頁／篩選寫回網址。用 replace：這些是同一個畫面的參數微調，
  // 每次都佔一筆瀏覽器歷史的話，返回鍵會卡在同一頁一直退篩選條件。
  useEffect(() => {
    const next = new URLSearchParams()
    if (pagination.page > 1) next.set('page', String(pagination.page))
    if (filters.keyword) next.set('keyword', filters.keyword)
    if (filters.categoryId) next.set('categoryId', filters.categoryId)
    if (onlineFilter !== 'all') next.set('status', onlineFilter)
    if (next.toString() !== searchParams.toString()) {
      setSearchParams(next, { replace: true })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.page, filters.keyword, filters.categoryId, onlineFilter])

  const handleToggleOnline = async (record, checked) => {
    setTogglingId(record._id)
    try {
      await toggleProductOnline(record._id, {
        onlineStatus: checked ? 'online' : 'offline'
      })
      message.success(checked ? '商品已上架' : '商品已下架')
      fetchProducts(pagination.page)
    } catch (e) {
      // 後端會擋下缺圖片、沒售價、商品已停用等情況，訊息很具體，直接顯示
      message.error(getApiErrorMessage(e, '切換上架狀態失敗'))
    } finally {
      setTogglingId(null)
    }
  }

  // 帶著「從哪個列表、哪一頁進來的」進編輯頁，讓返回鍵在重整、
  // 或直接貼網址開啟（沒有站內歷史）時也回得到正確的列表狀態。
  const goEdit = (id) =>
    navigate(`/inventory/products/edit/${id}`, {
      state: { from: `${location.pathname}${location.search}` }
    })

  // 上下架篩選在前端做：後端 API 是既有的庫存商品列表，
  // 加 query 參數會影響到庫存模組，這裡的資料量（單頁 20 筆）也不需要後端篩
  const visibleProducts = products.filter((p) => {
    if (onlineFilter === 'online') return p.onlineStatus === 'online'
    if (onlineFilter === 'offline') return p.onlineStatus !== 'online'
    return true
  })

  /** 判斷能不能上架與原因（表格與手機卡片共用） */
  const getBlockInfo = (r) => {
    const blocked =
      r.status !== 'active' ||
      !r.price ||
      !r.images?.length ||
      (r.hasVariants && !r.variantCount)
    const reason =
      r.status !== 'active'
        ? '商品已停用'
        : !r.price
          ? '尚未設定售價'
          : !r.images?.length
            ? '尚未上傳圖片'
            : '沒有啟用中的規格'
    return { blocked, reason }
  }

  const columns = [
    {
      title: '商品',
      width: 280,
      fixed: 'left',
      render: (_, r) => {
        // 資料庫存的是相對路徑，要補上圖床網域才顯示得出來
        const imageUrl = getProductImageUrl(r.images?.[0])
        return (
        <Space>
          {imageUrl
            ? (
              <Image
                src={imageUrl}
                width={44}
                height={44}
                style={{ objectFit: 'cover', borderRadius: 6 }}
                preview={false}
                fallback='data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI0NCIgaGVpZ2h0PSI0NCI+PHJlY3Qgd2lkdGg9IjQ0IiBoZWlnaHQ9IjQ0IiBmaWxsPSIjZjBmMGYwIi8+PC9zdmc+'
              />
              )
            : (
              <div
                style={{
                  width: 44,
                  height: 44,
                  borderRadius: 6,
                  background: '#f0f0f0',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <AppstoreOutlined style={{ color: '#bbb' }} />
              </div>
              )}
          <Space direction='vertical' size={0}>
            <Text>{r.name || r.title}</Text>
            <Space size={4}>
              <Text type='secondary' style={{ fontSize: 12 }}>
                {r.categoryName}
              </Text>
              {r.status !== 'active' && <Tag color='default'>已停用</Tag>}
            </Space>
          </Space>
        </Space>
        )
      }
    },
    {
      title: '售價',
      dataIndex: 'price',
      width: 110,
      align: 'right',
      render: (v) => money(v)
    },
    {
      title: '庫存',
      dataIndex: 'stock',
      width: 100,
      render: (v, r) => (
        <Space direction='vertical' size={0}>
          <Text strong={v <= 0} type={v <= 0 ? 'danger' : undefined}>
            {v}
          </Text>
          {r.hasVariants && (
            <Text type='secondary' style={{ fontSize: 11 }}>
              {r.variantCount} 種規格加總
            </Text>
          )}
        </Space>
      )
    },
    {
      title: '規格',
      width: 130,
      render: (_, r) => (
        <Space direction='vertical' size={2}>
          {r.hasVariants
            ? <Tag color='blue'>{r.variantCount} 種規格</Tag>
            : <Text type='secondary' style={{ fontSize: 12 }}>單一庫存</Text>}
          {canManage && (
            <Button
              size='small'
              type='link'
              style={{ padding: 0, height: 'auto' }}
              icon={<SettingOutlined />}
              onClick={() => setVariantProduct(r)}
            >
              規格設定
            </Button>
          )}
        </Space>
      )
    },
    {
      title: '線上販售',
      width: 120,
      render: (_, r) => {
        const { blocked, reason } = getBlockInfo(r)

        return (
          <Tooltip
            title={
              r.onlineStatus !== 'online' && blocked
                ? `無法上架：${reason}`
                : ''
            }
          >
            <Space direction='vertical' size={2}>
              <Switch
                checked={r.onlineStatus === 'online'}
                loading={togglingId === r._id}
                disabled={!canManage || (blocked && r.onlineStatus !== 'online')}
                checkedChildren='販售中'
                unCheckedChildren='未上架'
                onChange={(c) => handleToggleOnline(r, c)}
              />
              {r.onlineStatus !== 'online' && blocked && (
                <Text type='danger' style={{ fontSize: 11 }}>
                  {reason}
                </Text>
              )}
            </Space>
          </Tooltip>
        )
      }
    },
    {
      title: '',
      width: 90,
      render: (_, r) => (
        <Button size='small' onClick={() => goEdit(r._id)}>
          編輯
        </Button>
      )
    }
  ]

  if (!hasFeature('shopOrder')) {
    return (
      <PageWrapper>
        <Result
          status='warning'
          title='您的方案尚未開通線上商店'
          subTitle='請升級至高階方案以使用線上商店功能。'
        />
      </PageWrapper>
    )
  }

  return (
    <PageWrapper>
      <ContentWrapper>
        <Card style={{ borderRadius: 16, border: '1px solid #eef0f5' }}>
          <ShopHint
            tone='info'
            title='上架後客人才看得到'
            style={{ marginBottom: 16 }}
          >
            這裡列出的是您庫存中的所有商品。內部耗材（藥水、包材等）請保持未上架，只把要在網路上販售的商品切換為「販售中」。
          </ShopHint>

          <FilterBar>
            <Segmented
              block={isMobile}
              style={isMobile ? { width: '100%' } : undefined}
              value={onlineFilter}
              onChange={setOnlineFilter}
              options={[
                { label: `全部 (${statusCounts.total})`, value: 'all' },
                { label: `販售中 (${statusCounts.online})`, value: 'online' },
                {
                  label: `未上架 (${statusCounts.offline})`,
                  value: 'offline'
                }
              ]}
            />
            <Input
              allowClear
              style={{ width: isMobile ? '100%' : 220 }}
              prefix={<SearchOutlined />}
              placeholder='商品名稱'
              value={filters.keyword}
              onChange={(e) =>
                setFilters((f) => ({ ...f, keyword: e.target.value }))}
              onPressEnter={() => fetchProducts(1)}
            />
            <ResponsiveSelect
              allowClear
              style={{ width: isMobile ? '100%' : 160 }}
              placeholder='商品分類'
              value={filters.categoryId}
              onChange={(v) => setFilters((f) => ({ ...f, categoryId: v }))}
              options={categories.map((c) => ({
                value: c._id,
                label: c.name
              }))}
            />
            <Button
              type='primary'
              style={isMobile ? { flex: 1 } : undefined}
              onClick={() => fetchProducts(1)}
            >
              查詢
            </Button>
            <Button
              icon={<ReloadOutlined />}
              style={isMobile ? { flex: 1 } : undefined}
              onClick={() => fetchProducts(pagination.page)}
            >
              重新整理
            </Button>
          </FilterBar>

          {isMobile
            ? (
              /* ── 手機版：一筆商品一張卡 ── */
              <Spin spinning={loading}>
                {visibleProducts.length === 0 && !loading
                  ? (
                    <Empty
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                      description={
                        onlineFilter === 'online'
                          ? '尚未有商品上架販售'
                          : '沒有符合條件的商品'
                      }
                    />
                    )
                  : (
                      visibleProducts.map((r) => {
                        const { blocked, reason } = getBlockInfo(r)
                        const imageUrl = getProductImageUrl(r.images?.[0])
                        return (
                          <MobileCard key={r._id}>
                            <div className='top'>
                              {imageUrl
                                ? <img className='thumb' src={imageUrl} alt='' />
                                : (
                                  <div className='thumb-empty'>
                                    <AppstoreOutlined />
                                  </div>
                                  )}
                              <div className='info'>
                                <div className='name'>
                                  {r.name || r.title}
                                  {r.status !== 'active' && (
                                    <Tag color='default' style={{ marginLeft: 6 }}>
                                      已停用
                                    </Tag>
                                  )}
                                </div>
                                <div className='meta'>
                                  {r.categoryName || '未分類'}
                                  {'　'}庫存{' '}
                                  <Text
                                    strong={r.stock <= 0}
                                    type={r.stock <= 0 ? 'danger' : undefined}
                                    style={{ fontSize: 12 }}
                                  >
                                    {r.stock}
                                  </Text>
                                  {r.hasVariants && `（${r.variantCount} 種規格加總）`}
                                </div>
                                <div className='price'>{money(r.price)}</div>
                              </div>
                            </div>
                            <div className='bottom'>
                              <div>
                                <Switch
                                  checked={r.onlineStatus === 'online'}
                                  loading={togglingId === r._id}
                                  disabled={
                                    !canManage ||
                                    (blocked && r.onlineStatus !== 'online')
                                  }
                                  checkedChildren='販售中'
                                  unCheckedChildren='未上架'
                                  onChange={(c) => handleToggleOnline(r, c)}
                                />
                                {r.onlineStatus !== 'online' && blocked && (
                                  <div className='blocked-reason'>
                                    無法上架：{reason}
                                  </div>
                                )}
                              </div>
                              <Space size={4}>
                                {canManage && (
                                  <Button
                                    size='small'
                                    icon={<SettingOutlined />}
                                    onClick={() => setVariantProduct(r)}
                                  >
                                    規格
                                  </Button>
                                )}
                                <Button
                                  size='small'
                                  onClick={() => goEdit(r._id)}
                                >
                                  編輯
                                </Button>
                              </Space>
                            </div>
                          </MobileCard>
                        )
                      })
                    )}
                <div style={{ textAlign: 'center', marginTop: 16 }}>
                  <Pagination
                    simple
                    current={pagination.page}
                    pageSize={pagination.pageSize}
                    total={pagination.total}
                    onChange={(p) => fetchProducts(p)}
                  />
                </div>
              </Spin>
              )
            : (
              <Table
                rowKey='_id'
                loading={loading}
                columns={columns}
                dataSource={visibleProducts}
                scroll={{ x: 900 }}
                locale={{
                  emptyText: (
                    <Empty
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                      description={
                        onlineFilter === 'online'
                          ? '尚未有商品上架販售'
                          : '沒有符合條件的商品'
                      }
                    />
                  )
                }}
                pagination={{
                  current: pagination.page,
                  pageSize: pagination.pageSize,
                  total: pagination.total,
                  showSizeChanger: false,
                  showTotal: (t) => `共 ${t} 筆商品`,
                  onChange: (p) => fetchProducts(p)
                }}
              />
              )}
        </Card>
      </ContentWrapper>

      <ProductVariantModal
        open={!!variantProduct}
        product={variantProduct}
        onClose={() => setVariantProduct(null)}
        onSaved={() => fetchProducts(pagination.page)}
      />
    </PageWrapper>
  )
}
